'use client';
import { useAuthStore } from '@/stores/auth-store';

import { useQuery } from '@tanstack/react-query';
import { createClient } from '@/lib/supabase/client';
import { getDefaultTenantId } from '@/lib/supabase/query-context';
import {
  SETTINGS_DELEGATION_PAGE_KEY,
  SETTINGS_DELEGATION_PREF_TYPE,
  emptySettingsDelegationPreference,
  parseSettingsDelegationPreference,
  type SettingsDelegationPreference,
} from '@/lib/settings/settings-delegation';

export function useSettingsDelegation() {
  const tenantId = useAuthStore((state) => state.user?.tenantId);
  return useQuery<SettingsDelegationPreference>({
    queryKey: ['settings-delegation', tenantId],
    enabled: Boolean(tenantId),
    queryFn: async () => {
      const sb = createClient();
      if (!tenantId || (await getDefaultTenantId()) !== tenantId)
        throw new Error('Settings tenant context changed.');
      const { data, error } = await sb
        .from('layout_preferences')
        .select('visible_keys')
        .eq('tenant_id', tenantId)
        .is('user_id', null)
        .eq('page_key', SETTINGS_DELEGATION_PAGE_KEY)
        .eq('pref_type', SETTINGS_DELEGATION_PREF_TYPE)
        .maybeSingle();
      if (error) throw error;
      if (!data?.visible_keys) return emptySettingsDelegationPreference();
      return parseSettingsDelegationPreference(data.visible_keys as Record<string, unknown>);
    },
    staleTime: 60_000,
  });
}
